import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import {
  defaultLng,
  defaultNS,
  fallbackLng,
  resources,
  supportedLngs,
} from "./config";

// Export hooks and types
export * from "./hooks/useTranslation";
export * from "./config";

// Initialize i18next
i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize with configuration
  .init({
    ns: Object.keys(resources[defaultLng]) as any,
    defaultNS,
    fallbackLng,
    supportedLngs,
    lng: defaultLng,
    fallbackNS: defaultNS,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      useSuspense: true,
      bindI18n: "languageChanged loaded", // Make sure components re-render on language change or resource loading
      bindI18nStore: "added removed", // Make sure components re-render when resources are added or removed
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
    // Lazy load resources
    resources: Object.keys(resources).reduce(
      (acc, lang) => {
        acc[lang] = Object.keys(
          resources[lang as keyof typeof resources]
        ).reduce(
          (nsAcc, ns) => {
            nsAcc[ns] = {}; // Empty object for now, will be loaded lazily
            return nsAcc;
          },
          {} as Record<string, any>
        );
        return acc;
      },
      {} as Record<string, Record<string, any>>
    ),
  });

// Function to load resources lazily
export const loadResourceBundle = async (
  lng: string,
  ns: string
): Promise<void> => {
  const lngResources = resources[lng as keyof typeof resources];
  if (!lngResources) return;

  const nsLoader = lngResources[ns as keyof typeof lngResources];
  if (!nsLoader) return;

  try {
    const bundle = await (nsLoader as any)();
    i18n.addResourceBundle(lng, ns, bundle.default || bundle, true, true);
  } catch (error) {
    console.error(`Failed to load ${lng}/${ns} bundle`, error);
  }
};

// Set document language when i18n is initialized and when language changes
document.documentElement.setAttribute("lang", i18n.language || defaultLng);

// Add language change handler to reload the page to ensure all translations are applied
i18n.on("languageChanged", (lng) => {
  document.documentElement.setAttribute("lang", lng);
  // Force update of all components using translations
  window.dispatchEvent(new Event("i18nextLanguageChanged"));
});

export default i18n;
